import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareHeart, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import SurveySidePanel from "@/components/SurveySidePanel";

/**
 * SurveyNudge — friendly, dismissible bottom-right toast asking caregivers
 * for the 3-min survey ONCE they've genuinely spent time in the app.
 *
 * Visibility rules (all must be true):
 *   - User is signed in (real user, not admin, not Prolific tester)
 *   - Has not yet submitted the survey (`!feedback_submitted_at`)
 *   - Has spent ≥ 15 minutes of active time on the app
 *   - Not currently on the /feedback page (don't nag them while they're filling it out)
 *   - Hasn't dismissed it in the last 7 days
 *
 * NEVER blocks the user. Always has an X to close. Stored dismissal lives
 * in localStorage so it persists across sessions but auto-revives after a
 * week so we get a fresh ask.
 */

const ACTIVE_THRESHOLD_SECONDS = 15 * 60;            // 15 min of real engagement
const WALLCLOCK_THRESHOLD_MS = 24 * 60 * 60 * 1000;  // 24h since first signin
const DISMISS_KEY = "mlcn_survey_nudge_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;      // 7 days

function trialWallclockElapsed(user) {
    // Original PMF trigger was "15 min active OR 24h wall-clock", but the
    // wall-clock arm was never wired into this nudge. Someone who signs in
    // 5 min a day for a week would never see the survey. Fixed here.
    const ts = user?.trial_started_at;
    if (!ts) return false;
    const started = new Date(ts).getTime();
    if (!Number.isFinite(started)) return false;
    return (Date.now() - started) >= WALLCLOCK_THRESHOLD_MS;
}

function wasRecentlyDismissed() {
    try {
        const v = localStorage.getItem(DISMISS_KEY);
        if (!v) return false;
        const ts = parseInt(v, 10);
        if (!Number.isFinite(ts)) return false;
        return (Date.now() - ts) < DISMISS_TTL_MS;
    } catch {
        return false;
    }
}

export default function SurveyNudge() {
    const { user } = useAuth();
    const location = useLocation();
    const [dismissedThisSession, setDismissedThisSession] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);

    // Never compete with the page the caregiver just landed on — wait 45s
    // of on-page time before the nudge may appear.
    const [pageSettled, setPageSettled] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setPageSettled(true), 45000);
        return () => clearTimeout(t);
    }, []);

    // Re-check the dismissal state on mount so a stale cross-tab dismissal
    // is respected. We don't poll — once visible, it stays visible until
    // the user X's it or navigates to /feedback.
    const [recentlyDismissed, setRecentlyDismissed] = useState(() => wasRecentlyDismissed());
    useEffect(() => {
        setRecentlyDismissed(wasRecentlyDismissed());
    }, [user]);

    if (!user || user === false) return null;
    if (user.role === "admin") return null;
    if (user.tester_code) return null;                  // Testers have their own panel
    if (user.demo_mode || localStorage.getItem("ml_demo_mode") === "1") return null; // Never nag investors in the demo

    // Side panel can be open even after a successful submit — show the
    // "thank you" state inside it until the user closes it. The toast
    // below is gated separately on feedback_submitted_at.
    const showToast = (
        pageSettled
        && !user.feedback_submitted_at
        && !dismissedThisSession
        && !recentlyDismissed
        && !location.pathname.startsWith("/feedback")
        // /chat: the toast covered the composer and send button at 390px —
        // never show it there.
        && !location.pathname.startsWith("/chat")
        && (
            (Number(user.active_time_seconds) || 0) >= ACTIVE_THRESHOLD_SECONDS
            || trialWallclockElapsed(user)
        )
    );

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* private mode */ }
        setDismissedThisSession(true);
    };

    return (
        <>
            <SurveySidePanel open={panelOpen} onOpenChange={setPanelOpen} />
            {showToast && (
                <div
                    data-testid="survey-nudge"
                    className="fixed z-40 animate-fade-in-up bg-cream-light border border-teal-soft shadow-lg p-4 bottom-0 left-0 right-0 rounded-t-2xl sm:bottom-4 sm:left-auto sm:right-4 sm:rounded-2xl sm:max-w-sm sm:w-80"
                    role="status"
                    aria-live="polite"
                >
                    <button
                        type="button"
                        onClick={dismiss}
                        data-testid="survey-nudge-dismiss"
                        aria-label="Dismiss"
                        className="absolute top-2 right-2 p-1 rounded-full text-[#6E7A74] hover:text-[#4C5551] hover:bg-cream-dark transition-colors"
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                    <div className="flex items-start gap-3 pr-4">
                        <div className="shrink-0 w-9 h-9 rounded-2xl bg-teal-soft flex items-center justify-center">
                            <MessageSquareHeart size={18} strokeWidth={1.7} className="text-teal-dark" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1F2624] leading-snug">
                                Got 3 minutes to help shape Memory Lane?
                            </p>
                            <p className="text-xs text-[#4C5551] mt-1 leading-relaxed">
                                Your honest answers tell us what to build next. No marketing list, no spam.
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setPanelOpen(true); dismiss(); }}
                                    data-testid="survey-nudge-take"
                                    className="text-xs font-medium bg-teal-dark hover:bg-teal-dark/90 text-white rounded-2xl px-3 py-1.5"
                                >
                                    Take the survey
                                </button>
                                <button
                                    type="button"
                                    onClick={dismiss}
                                    data-testid="survey-nudge-later"
                                    className="text-xs text-[#4C5551] hover:text-teal-dark"
                                >
                                    Maybe later
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
